<template>
  <div class="p-3 pt-5 rounded bg-body mb-5">
    <div class="row align-items-end mb-5">
      <div class="col-lg-2 mb-5 mb-lg-0">
        <date-field
          v-model="filters.fromDate"
          id="espRealization.fromDate"
          name="espRealization.fromDate"
          placeholder="Дата с"
        />
      </div>
      <div class="col-lg-2 mb-5 mb-lg-0">
        <date-field
          v-model="filters.toDate"
          id="espRealization.toDate"
          name="espRealization.toDate"
          placeholder="Дата по"
        />
      </div>
      <div class="col-lg-5"></div>
      <div class="col-lg-3 d-flex justify-content-end gap-2 mb-5 mb-lg-0">
        <button type="button" class="btn btn-sm btn-primary" @click="applyFilters">
          Показать
        </button>
        <button type="button" class="btn btn-sm btn-light-danger" @click="resetFilters">
          Сбросить
        </button>
      </div>
    </div>
    <loader v-if="loaded" :height="25" />
    <div v-show="!loaded">
      <apexchart
        type="line"
        height="300"
        :options="chartOptions"
        :series="series"
      />
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts'
import Loader from '@/components/Loader'
import DateField from '@/components/Fields/DateField'
import axios from 'axios'

export default {
  name: 'EspRealization',
  components: {
    Loader,
    DateField,
    apexchart: VueApexCharts
  },
  data() {
    return {
      loaded: false,
      defaultFromDate: '',
      defaultToDate: '',
      filters: {
        fromDate: '',
        toDate: ''
      },
      series: [],
      chartOptions: {
        chart: {
          type: 'line',
          stacked: false,
          toolbar: { show: false },
        },
        stroke: {
          width: [0, 2, 2],
          curve: 'smooth'
        },
        fill: {
          type: ['solid', 'gradient', 'solid'],
          gradient: {
            opacityFrom: 0.4,
            opacityTo: 0.05
          }
        },
        plotOptions: {
          bar: {
            columnWidth: '40%',
          }
        },
        xaxis: {
          categories: [],
        },
        yaxis: [
          {
            title: { text: 'Количество' },
            labels: {
              formatter: val => parseInt(val)
            }
          },
          {
            opposite: true,
            title: { text: 'кВт' }
          },
          {
            opposite: true,
            title: { text: 'Сумма' },
            labels: {
              formatter: val => {
                const formatted = Number(val || 0).toLocaleString('ru-RU', {
                  maximumFractionDigits: 0
                })
                return formatted.replace(/\u00A0/g, ' ')
              }
            }
          }
        ],
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter(val, opts) {
              const name = opts?.w?.globals?.seriesNames?.[opts.seriesIndex] || ''
              if (name === 'Общая сумма') {
                const formatted = Number(val || 0).toLocaleString('ru-RU', {
                  maximumFractionDigits: 0
                })
                return formatted.replace(/\u00A0/g, ' ')
              }
              if (name === 'кВт') return `${val} кВт`
              if (name === 'Количество') return `${parseInt(val)} шт`
              return val
            }
          }
        },
        legend: {
          position: 'top'
        },
        title: {
          text: 'Реализация',
          align: 'left',
        }
      }
    }
  },
  mounted() {
    this.setDefaultDates()
    this.getEspRealization()
  },
  methods: {
    formatDateForInput(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    setDefaultDates() {
      const now = new Date()
      const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate())

      this.defaultFromDate = this.formatDateForInput(monthAgo)
      this.defaultToDate = this.formatDateForInput(now)

      this.filters.fromDate = this.defaultFromDate
      this.filters.toDate = this.defaultToDate
    },
    normalizeRange(fromDateStr, toDateStr) {
      const fromStr = fromDateStr || this.defaultFromDate
      const toStr = toDateStr || this.defaultToDate

      const from = new Date(`${fromStr}T00:00:00`)
      const to = new Date(`${toStr}T23:59:59.999`)

      let fromDate = from
      let toDate = to
      if (fromDate.getTime() > toDate.getTime()) {
        const tmp = fromDate
        fromDate = toDate
        toDate = tmp
      }

      return { fromDate, toDate }
    },
    applyFilters() {
      this.getEspRealization()
    },
    resetFilters() {
      this.filters.fromDate = this.defaultFromDate
      this.filters.toDate = this.defaultToDate
      this.getEspRealization()
    },
    getEspRealization() {
      this.loaded = true

      const { fromDate: from, toDate: to } = this.normalizeRange(
        this.filters.fromDate,
        this.filters.toDate
      )

      const fromDate = from.getTime() // миллисекунды
      const toDate = to.getTime() // миллисекунды

      axios.get('order-service/api/v1/admin/realization/esp', {
        params: { fromDate, toDate }
      })
        .then(res => {
          const categories = []
          const counts = []
          const volumes = []
          const totals = []

          const dateFormatter = new Intl.DateTimeFormat('ru-RU', {
            day: '2-digit',
            month: '2-digit'
          })

          const sorted = [...res.data].sort((a, b) => new Date(a.date) - new Date(b.date))

          for (const item of sorted) {
            const formattedDate = dateFormatter.format(new Date(item.date))
            categories.push(formattedDate)
            counts.push(item.count)
            volumes.push(item.volume)
            totals.push(item.totalAmount)
          }

          this.series = [
            {
              name: 'Количество',
              type: 'column',
              data: counts
            },
            {
              name: 'кВт',
              type: 'area',
              data: volumes
            },
            {
              name: 'Общая сумма',
              type: 'line',
              data: totals
            }
          ]

          this.chartOptions = {
            ...this.chartOptions,
            xaxis: {
              categories
            },
            title: {
              text: `Реализация с ${dateFormatter.format(from)} по ${dateFormatter.format(to)}`,
              align: 'left'
            }
          }
        })
        .catch(err => {
          console.error('Ошибка при запросе getEspRealization:', err)
        })
        .finally(() => {
          this.loaded = false
        })
    }
  }
}
</script>
